import {
    ComponentRef,
    createNgModule,
    Injectable,
    Injector,
    NgModuleRef,
    Type,
    ViewContainerRef,
} from '@angular/core';
import type { IUserControlResolver } from '@se/cp.engine';

@Injectable()
export class UserControlService implements IUserControlResolver {
    modules = new Map<string, NgModuleRef<any>>();

    constructor(private injector: Injector) {}

    async loadComponent(vcr: ViewContainerRef, idUserControl: string): Promise<ComponentRef<any>> {
        switch (idUserControl) {
            case 'idExampleUserControl':
                const { CpExampleUserControlComponent, CpExampleUserControlModule } = await import(
                    '@se/cp.usercontrols/cp-example-user-control/public-api'
                );
                return this.createComponent(
                    idUserControl,
                    vcr,
                    CpExampleUserControlComponent,
                    CpExampleUserControlModule,
                );

            case 'idUserControlEcgData':
                const { CpUcEcgDataComponent, CpUcEcgDataModule } = await import(
                    '@se/cp.usercontrols/cp-uc-ecg-data/public-api'
                );
                return this.createComponent(idUserControl, vcr, CpUcEcgDataComponent, CpUcEcgDataModule);

            case 'idUserControlDDeviceViewer':
                const { CpUcDDeviceViewerComponent, CpUcDDeviceViewerModule } = await import(
                    '@se/cp.usercontrols/cp-uc-ddevice-viewer/public-api'
                );
                return this.createComponent(idUserControl, vcr, CpUcDDeviceViewerComponent, CpUcDDeviceViewerModule);

            case 'idUserControlMeasureDetails':
                const { CpUcMeasureDetailsComponent, CpUcMeasureDetailsModule } = await import(
                    '@se/cp.usercontrols/cp-uc-measure-details/public-api'
                );
                return this.createComponent(idUserControl, vcr, CpUcMeasureDetailsComponent, CpUcMeasureDetailsModule);

            case 'idUserControlMeasureGrid': {
                const { CpUcMeasureGridComponent, CpUcMeasureGridModule } = await import(
                    '@se/cp.usercontrols/cp-uc-measure-grid/public-api'
                );
                const component = await this.createComponent(
                    idUserControl,
                    vcr,
                    CpUcMeasureGridComponent,
                    CpUcMeasureGridModule,
                );
                return Promise.resolve(component);
            }
            case 'idUserControlUs2ai': {
                const { CpUcUs2aiComponent, CpUcUs2aiModule } = await import(
                    '@se/cp.usercontrols/cp-uc-us2ai/public-api'
                );
                const component = await this.createComponent(idUserControl, vcr, CpUcUs2aiComponent, CpUcUs2aiModule);
                return Promise.resolve(component);
            }
            case 'idUserControlSportVisit':
                const { CpUcSportVisitModule, CpUcSportVisitComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-sport-visit/public-api'
                );
                return this.createComponent(idUserControl, vcr, CpUcSportVisitComponent, CpUcSportVisitModule);
            case 'idUserControlSportImport':
                const { CpUcSportImportModule, CpUcSportImportGridComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-sport-import/public-api'
                );
                return this.createComponent(idUserControl, vcr, CpUcSportImportGridComponent, CpUcSportImportModule);
            case 'idUserControlMultiStageMap':
                const { CpUcEcostressMapModule, CpUcEcostressMapComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-ecostress-map/public-api'
                );
                return this.createComponent(idUserControl, vcr, CpUcEcostressMapComponent, CpUcEcostressMapModule);

            case 'idUserControlAddendum':
                const { CpUcAddendumModule, CpUcAddendumComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-addendum/public-api'
                );
                return this.createComponent(idUserControl, vcr, CpUcAddendumComponent, CpUcAddendumModule);

            case 'idUserControlClickDoc':
                const { CpClickDocModule, CpUcClickDocComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-click-doc/public-api'
                );
                return this.createComponent(idUserControl, vcr, CpUcClickDocComponent, CpClickDocModule);

            default:
                throw new Error('Error - No idUserControl');
        }
    }

    private async createComponent<C, M>(name: string, vcr: ViewContainerRef, component: Type<C>, module: Type<M>) {
        const moduleRef = await this.createModule(name, module);
        vcr.clear();
        return vcr.createComponent(component, { index: 0, injector: moduleRef?.injector });
    }

    private async createModule<T>(moduleName: string, module: Type<T>) {
        if (this.modules.has(moduleName)) {
            return this.modules.get(moduleName);
        }
        const moduleRef = createNgModule(module, this.injector);
        this.modules.set(moduleName, moduleRef);
        return moduleRef;
    }
}
